import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { adminApi, apiError } from "../lib/api";
import { useAdminAuth } from "../context/AdminAuth";
import { useRefetchOnFocus } from "../hooks/useRefetchOnFocus";
import { ArrowLeft, Plus, Minus, RefreshCw, ExternalLink, Mail, CheckCircle2, XCircle, Clock, ShieldCheck, Ticket, Trash2, Scissors, Coins, Pencil, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

function EmailStatus({ status }) {
  const map = {
    sent: { c: "bg-emerald-50 text-emerald-700 border-emerald-200", i: CheckCircle2, t: "Gesendet" },
    failed: { c: "bg-rose-50 text-rose-700 border-rose-200", i: XCircle, t: "Fehlgeschlagen" },
    pending: { c: "bg-amber-50 text-amber-700 border-amber-200", i: Clock, t: "Ausstehend" },
    skipped: { c: "bg-stone-100 text-stone-600 border-stone-200", i: Clock, t: "Übersprungen" },
  };
  const m = map[status] || map.pending;
  const I = m.i;
  return <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full border ${m.c}`}><I className="h-3 w-3" />{m.t}</span>;
}

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; }
}

function eur(v) {
  return Number(v || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
}

export default function AdminCustomerDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { admin } = useAdminAuth();
  const [data, setData] = useState(null);
  const [dlg, setDlg] = useState(null); // "deduct"
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [voucherOpen, setVoucherOpen] = useState(false);
  const [voucher, setVoucher] = useState({ code: "", description: "", expires_at: "" });
  const [catalog, setCatalog] = useState({ categories: [], services: [] });
  const [visitOpen, setVisitOpen] = useState(false);
  const [selected, setSelected] = useState([]); // [{service_id,name,category,price}]
  const [points, setPoints] = useState("");
  const [comment, setComment] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ first_name: "", last_name: "", phone: "", email: "", marketing_consent: false });
  const [editVisitOpen, setEditVisitOpen] = useState(false);
  const [editTxnId, setEditTxnId] = useState(null);
  const [editSelected, setEditSelected] = useState([]);
  const [editPoints, setEditPoints] = useState("");
  const [editComment, setEditComment] = useState("");

  const load = useCallback(async () => {
    try {
      const [r, s] = await Promise.all([
        adminApi.get(`/admin/customers/${id}`),
        adminApi.get(`/admin/services`),
      ]);
      setData(r.data);
      setCatalog(s.data);
    } catch (e) { toast.error(apiError(e)); }
  }, [id]);

  useEffect(() => {
    if (admin === false) nav("/admin");
    if (admin) load();
  }, [admin, load, nav]);
  useRefetchOnFocus(load);

  const submitChange = async () => {
    const amt = parseInt(amount, 10);
    if (!amt || amt <= 0) { toast.error("Bitte eine gültige Punktzahl eingeben"); return; }
    setBusy(true);
    try {
      const ep = dlg === "add" ? "add-points" : "deduct-points";
      await adminApi.post(`/admin/customers/${id}/${ep}`, { amount: amt, reason });
      toast.success(dlg === "add" ? "Punkte hinzugefügt & E-Mail versendet" : "Punkte abgezogen & E-Mail versendet");
      setDlg(null); setAmount(""); setReason("");
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const resend = async (txnId) => {
    try { await adminApi.post(`/admin/transactions/${txnId}/resend-email`); toast.success("E-Mail erneut gesendet"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const openPortal = async () => {
    try { const r = await adminApi.post(`/admin/customers/${id}/portal-link`); window.open(r.data.url, "_blank"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const createVoucher = async () => {
    if (!voucher.code.trim()) { toast.error("Bitte einen Gutschein-Code eingeben"); return; }
    try {
      await adminApi.post(`/admin/customers/${id}/vouchers`, voucher);
      toast.success("Gutschein ausgestellt");
      setVoucherOpen(false);
      setVoucher({ code: "", description: "", expires_at: "" });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const deleteVoucher = async (vid) => {
    try { await adminApi.delete(`/admin/vouchers/${vid}`); toast.success("Gutschein entfernt"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const deleteCustomer = async () => {
    if (confirmText !== "LÖSCHEN") { toast.error('Bitte „LÖSCHEN" zur Bestätigung eingeben'); return; }
    try {
      await adminApi.delete(`/admin/customers/${id}`);
      toast.success("Kundin wurde erfolgreich gelöscht.");
      nav("/admin");
    } catch (e) { toast.error(apiError(e)); }
  };

  const openEdit = () => {
    setEditForm({ first_name: c.first_name, last_name: c.last_name || "", phone: c.phone || "", email: c.email, marketing_consent: c.marketing_consent });
    setEditOpen(true);
  };
  const saveEdit = async () => {
    try {
      await adminApi.put(`/admin/customers/${id}`, editForm);
      toast.success("Kundin aktualisiert");
      setEditOpen(false);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggleService = (svc, catName) => {
    setSelected((prev) => prev.some((x) => x.service_id === svc.id)
      ? prev.filter((x) => x.service_id !== svc.id)
      : [...prev, { service_id: svc.id, name: svc.name, category: catName, price: "" }]);
  };
  const setPrice = (sid, price) => setSelected((prev) => prev.map((x) => x.service_id === sid ? { ...x, price } : x));
  const visitTotal = selected.reduce((s, x) => s + (parseFloat(x.price) || 0), 0);

  const saveVisit = async () => {
    const pts = parseInt(points, 10);
    if (selected.length === 0) { toast.error("Bitte mindestens eine Dienstleistung auswählen"); return; }
    if (!pts || pts <= 0) { toast.error("Bitte Punkte für diesen Besuch eingeben"); return; }
    setBusy(true);
    try {
      await adminApi.post(`/admin/customers/${id}/visits`, {
        services: selected.map((x) => ({ service_id: x.service_id, name: x.name, category: x.category, price: parseFloat(x.price) || 0 })),
        points: pts,
        comment,
      });
      toast.success("Besuch gespeichert & Punkte gutgeschrieben");
      setVisitOpen(false); setSelected([]); setPoints(""); setComment("");
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const openEditVisit = (t) => {
    setEditTxnId(t.id);
    setEditSelected((t.services || []).map((s, i) => ({
      service_id: s.service_id || `x-${i}-${Math.random().toString(36).slice(2, 8)}`,
      name: s.name, category: s.category || "", price: String(s.price ?? ""),
    })));
    setEditPoints(String(t.amount ?? ""));
    setEditComment(t.comment || "");
    setEditVisitOpen(true);
  };
  const toggleEditService = (svc, catName) => {
    setEditSelected((prev) => prev.some((x) => x.service_id === svc.id)
      ? prev.filter((x) => x.service_id !== svc.id)
      : [...prev, { service_id: svc.id, name: svc.name, category: catName, price: "" }]);
  };
  const setEditPrice = (sid, price) => setEditSelected((prev) => prev.map((x) => x.service_id === sid ? { ...x, price } : x));
  const removeEditService = (sid) => setEditSelected((prev) => prev.filter((x) => x.service_id !== sid));
  const editVisitTotal = editSelected.reduce((s, x) => s + (parseFloat(x.price) || 0), 0);
  const saveEditVisit = async () => {
    const pts = parseInt(editPoints, 10);
    if (Number.isNaN(pts) || pts < 0) { toast.error("Bitte gültige Punkte eingeben"); return; }
    if (editSelected.length === 0) { toast.error("Mindestens eine Dienstleistung auswählen"); return; }
    try {
      await adminApi.put(`/admin/transactions/${editTxnId}/visit`, {
        services: editSelected.map((x) => ({
          service_id: String(x.service_id).startsWith("x-") ? null : x.service_id,
          name: x.name, category: x.category, price: parseFloat(x.price) || 0,
        })),
        points: pts,
        comment: editComment,
      });
      toast.success("Änderungen gespeichert");
      setEditVisitOpen(false);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!data) return <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center text-stone-400">Lädt…</div>;
  const c = data.customer;
  const txns = data.transactions;
  const activeCats = (catalog.categories || []).filter((x) => x.active).sort((a, b) => a.order - b.order);

  return (
    <div className="min-h-screen bg-[#FAF7F2]">
      <header className="bg-[#4A1D24] text-stone-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-3">
          <button data-testid="back-button" onClick={() => nav("/admin")} className="p-2 rounded-lg hover:bg-white/10"><ArrowLeft className="h-5 w-5" /></button>
          <div>
            <h1 className="font-serif text-2xl">{c.first_name} {c.last_name}</h1>
            <p className="text-xs text-stone-300/70">{c.email}</p>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 grid lg:grid-cols-12 gap-6 items-start">
        {/* Left: overview + actions */}
        <div className="lg:col-span-4 space-y-5">
          <div className="rounded-2xl bg-white/90 border border-stone-200/80 shadow-sm p-5">
            <p className="text-xs uppercase tracking-widest text-stone-400">Aktueller Punktestand</p>
            <p className="font-mono text-4xl font-bold text-[#4A1D24] mt-1" data-testid="detail-points">{c.points}</p>
            <div className="flex items-center gap-2 mt-2">
              <span className="text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full border border-amber-400/50 bg-amber-500/15 text-amber-900">{c.tier}</span>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-4">
              <Button data-testid="add-visit-button" onClick={() => setVisitOpen(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white"><Scissors className="h-4 w-4 mr-1" /> Besuch</Button>
              <Button data-testid="deduct-points-button" onClick={() => setDlg("deduct")} variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50"><Minus className="h-4 w-4 mr-1" /> Abziehen</Button>
            </div>
            <Button data-testid="open-portal-button" onClick={openPortal} variant="outline" className="w-full mt-2"><ExternalLink className="h-4 w-4 mr-1.5" /> Kundenportal öffnen</Button>
            <Button data-testid="edit-customer-button" onClick={openEdit} variant="outline" className="w-full mt-2"><Pencil className="h-4 w-4 mr-1.5" /> Kundin bearbeiten</Button>
          </div>

          <div className="rounded-2xl bg-white/90 border border-stone-200/80 shadow-sm p-5 space-y-3 text-sm">
            <Row label="E-Mail" value={c.email} />
            <Row label="Telefon" value={c.phone || "—"} />
            <Row label="Letzter Besuch" value={fmt(c.last_visit)} />
            <Row label="Letzte Punkteänderung" value={fmt(c.last_points_change)} />
            <div className="flex items-center justify-between">
              <span className="text-stone-500">Status letzte E-Mail</span>
              <EmailStatus status={data.last_email_status || "pending"} />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-stone-500 flex items-center gap-1"><ShieldCheck className="h-4 w-4" /> Marketing-Einwilligung</span>
              <span data-testid="detail-marketing-consent" className={`text-[11px] font-medium px-2 py-0.5 rounded-full border ${c.marketing_consent ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-stone-100 text-stone-600 border-stone-200"}`}>
                {c.marketing_consent ? "Erteilt" : "Nicht erteilt"}
              </span>
            </div>
          </div>

          <div className="rounded-2xl bg-white/90 border border-stone-200/80 shadow-sm p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-[#4A1D24]"><Ticket className="h-5 w-5" /><h3 className="font-semibold">Gutscheine</h3></div>
              <Button data-testid="add-voucher-button" size="sm" variant="outline" onClick={() => setVoucherOpen(true)}><Plus className="h-4 w-4 mr-1" /> Ausstellen</Button>
            </div>
            <div className="mt-3 space-y-2" data-testid="voucher-list">
              {(data.vouchers || []).length === 0 && <p className="text-xs text-stone-400">Keine Gutscheine.</p>}
              {(data.vouchers || []).map((v) => (
                <div key={v.id} data-testid={`voucher-item-${v.id}`} className="flex items-center gap-2 rounded-lg border border-dashed border-[#C59B6C] p-2.5">
                  <Ticket className="h-4 w-4 text-[#C59B6C] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-mono text-sm font-semibold text-[#4A1D24] truncate">{v.code}</p>
                    <p className="text-xs text-stone-500 truncate">{v.description}{v.expires_at ? ` · bis ${v.expires_at}` : ""}</p>
                  </div>
                  <button data-testid={`delete-voucher-${v.id}`} onClick={() => deleteVoucher(v.id)} className="p-1.5 rounded-lg hover:bg-rose-50 text-rose-600 shrink-0"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          </div>

          {admin?.role === "admin" && (
            <div className="rounded-2xl bg-white/90 border border-rose-200 shadow-sm p-5" data-testid="danger-zone">
              <h3 className="font-semibold text-rose-700 flex items-center gap-2"><Trash2 className="h-4 w-4" /> Gefahrenzone</h3>
              <p className="text-xs text-stone-500 mt-1">Kundin dauerhaft löschen. Zugang, Punkte, QR-Code und Gutscheine werden entfernt.</p>
              <Button data-testid="delete-customer-button" onClick={() => { setConfirmText(""); setDeleteOpen(true); }} variant="outline" className="w-full mt-3 border-rose-300 text-rose-700 hover:bg-rose-50">Kundin löschen</Button>
            </div>
          )}
        </div>

        {/* Right: audit log */}
        <div className="lg:col-span-8 rounded-2xl bg-white/90 border border-stone-200/80 shadow-sm">
          <div className="p-5 border-b border-stone-100 flex items-center justify-between">
            <div>
              <h2 className="font-serif text-2xl text-stone-900">Interne Kundenhistorie &amp; Audit-Log</h2>
              <p className="text-xs text-stone-400">Dienstleistungen, Preise &amp; Notizen – nur intern sichtbar</p>
            </div>
            <button data-testid="refresh-detail-button" onClick={load} className="p-2 rounded-lg hover:bg-stone-100 text-stone-500"><RefreshCw className="h-4 w-4" /></button>
          </div>
          <div className="divide-y divide-stone-100" data-testid="audit-log-table">
            {txns.length === 0 && <p className="p-6 text-sm text-stone-400">Noch keine Punkteänderungen.</p>}
            {txns.map((t) => (
              <div key={t.id} className="p-4 flex items-start gap-4" data-testid={`audit-row-${t.id}`}>
                <div className={`shrink-0 h-9 w-9 rounded-full flex items-center justify-center ${t.type === "earn" ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>
                  {t.type === "earn" ? <Plus className="h-4 w-4" /> : <Minus className="h-4 w-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-medium text-stone-900">
                      {t.type === "earn" ? "+" : "−"}{t.amount} Punkte
                      <span className="text-stone-400 font-normal text-sm"> → {t.balance_after} P</span>
                    </p>
                    <EmailStatus status={t.email_status} />
                  </div>
                  {t.services && t.services.length > 0 ? (
                    <div className="mt-1.5 rounded-lg bg-stone-50 border border-stone-100 p-2.5" data-testid={`visit-services-${t.id}`}>
                      {t.services.map((s, i) => (
                        <div key={i} className="flex justify-between text-sm text-stone-600 py-0.5">
                          <span>{s.name}</span>
                          <span className="font-mono">{eur(s.price)}</span>
                        </div>
                      ))}
                      <div className="flex justify-between text-sm font-semibold text-stone-900 border-t border-stone-200 mt-1 pt-1">
                        <span>Gesamtpreis</span>
                        <span className="font-mono">{eur(t.total_price)}</span>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-stone-500 mt-0.5">{t.reward_name || t.reason || "Kein Grund angegeben"}</p>
                  )}
                  {t.comment ? <p className="text-xs text-amber-800 mt-1"><span className="font-semibold">Interne Notiz:</span> „{t.comment}"</p> : null}
                  <p className="text-xs text-stone-400 mt-1">{fmt(t.created_at)} · {t.staff_name}</p>
                  <div className="flex items-center gap-2 mt-2">
                    {t.services && t.services.length > 0 && (
                      <Button data-testid={`edit-visit-button-${t.id}`} onClick={() => openEditVisit(t)} size="sm" variant="outline" className="h-7 text-xs border-[#C59B6C] text-[#4A1D24] hover:bg-amber-50">
                        <Pencil className="h-3 w-3 mr-1" /> Bearbeiten
                      </Button>
                    )}
                    {t.email_status === "failed" && (
                      <Button data-testid={`resend-email-button-${t.id}`} onClick={() => resend(t.id)} size="sm" variant="outline" className="h-7 text-xs border-rose-300 text-rose-700 hover:bg-rose-50">
                        <Mail className="h-3 w-3 mr-1" /> E-Mail erneut senden
                      </Button>
                    )}
                    {t.edited_at && <span className="text-[10px] text-stone-400">bearbeitet</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{dlg === "add" ? "Punkte hinzufügen" : "Punkte abziehen"}</DialogTitle>
            <DialogDescription>Die Kundin wird automatisch per E-Mail über die Änderung informiert.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Anzahl Punkte</Label><Input data-testid="points-amount-input" type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} /></div>
            <div><Label className="text-xs">Grund / Dienstleistung / Prämie</Label><Textarea data-testid="points-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="z. B. Haarschnitt & Föhnen" /></div>
            <p className="text-xs text-stone-400">Die Kundin wird automatisch per E-Mail informiert.</p>
          </div>
          <DialogFooter>
            <Button data-testid="confirm-points-button" onClick={submitChange} disabled={busy} className={dlg === "add" ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-rose-600 hover:bg-rose-700 text-white"}>
              {busy ? "Speichern…" : "Bestätigen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={voucherOpen} onOpenChange={setVoucherOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Gutschein ausstellen</DialogTitle>
            <DialogDescription>Der Gutschein erscheint im Kundenportal der Kundin.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Code</Label><Input data-testid="voucher-code-input" value={voucher.code} onChange={(e) => setVoucher({ ...voucher, code: e.target.value.toUpperCase() })} placeholder="z. B. WILLKOMMEN10" /></div>
            <div><Label className="text-xs">Beschreibung</Label><Input data-testid="voucher-desc-input" value={voucher.description} onChange={(e) => setVoucher({ ...voucher, description: e.target.value })} placeholder="10€ Rabatt beim nächsten Besuch" /></div>
            <div><Label className="text-xs">Gültig bis (optional)</Label><Input data-testid="voucher-expiry-input" value={voucher.expires_at} onChange={(e) => setVoucher({ ...voucher, expires_at: e.target.value })} placeholder="31.12.2026" /></div>
          </div>
          <DialogFooter>
            <Button data-testid="confirm-voucher-button" onClick={createVoucher} className="bg-[#4A1D24] hover:bg-[#3a161c] text-white">Ausstellen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={visitOpen} onOpenChange={setVisitOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Besuch hinzufügen</DialogTitle>
            <DialogDescription>Dienstleistungen wählen, Preise eintragen und Punkte selbst vergeben.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-3" data-testid="visit-service-picker">
              {activeCats.map((cat) => {
                const svcs = (catalog.services || []).filter((s) => s.active && s.category_id === cat.id).sort((a, b) => a.order - b.order);
                if (svcs.length === 0) return null;
                return (
                  <div key={cat.id}>
                    <p className="text-[11px] font-semibold uppercase tracking-widest text-amber-800/80 mb-1.5">{cat.name}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {svcs.map((s) => {
                        const on = selected.some((x) => x.service_id === s.id);
                        return (
                          <button key={s.id} type="button" data-testid={`service-chip-${s.id}`} onClick={() => toggleService(s, cat.name)}
                            className={`text-xs px-2.5 py-1.5 rounded-full border transition-colors ${on ? "bg-[#4A1D24] text-white border-[#4A1D24]" : "bg-white text-stone-600 border-stone-200 hover:border-[#C59B6C]"}`}>
                            {s.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            {selected.length > 0 && (
              <div className="rounded-xl border border-stone-200 p-3 space-y-2" data-testid="visit-price-fields">
                {selected.map((x) => (
                  <div key={x.service_id} className="flex items-center gap-2">
                    <span className="flex-1 text-sm text-stone-700 min-w-0 truncate">{x.name}</span>
                    <div className="relative w-28 shrink-0">
                      <Input data-testid={`price-input-${x.service_id}`} type="number" min="0" step="0.01" value={x.price} onChange={(e) => setPrice(x.service_id, e.target.value)} className="pr-6 text-right" placeholder="0,00" />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-400 text-sm">€</span>
                    </div>
                  </div>
                ))}
                <div className="flex justify-between items-center border-t border-stone-200 pt-2 font-semibold text-stone-900">
                  <span>Gesamtpreis</span>
                  <span data-testid="visit-total-price" className="font-mono">{eur(visitTotal)}</span>
                </div>
              </div>
            )}

            <div className="rounded-xl bg-amber-50 border border-amber-200 p-3">
              <Label className="text-xs flex items-center gap-1 text-amber-900"><Coins className="h-3.5 w-3.5" /> Punkte für diesen Besuch</Label>
              <Input data-testid="visit-points-input" type="number" min="1" value={points} onChange={(e) => setPoints(e.target.value)} className="mt-1 bg-white" placeholder="z. B. 25" />
              <p className="text-[11px] text-amber-800/80 mt-1">Punkte sind unabhängig vom Preis – Sie bestimmen sie selbst.</p>
            </div>

            <div><Label className="text-xs">Interne Notiz / Bemerkung (nur intern sichtbar)</Label><Textarea data-testid="visit-comment-input" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="z. B. Ansatz dunkel, nächstes Mal Farbton 6.1 verwenden." /></div>
          </div>
          <DialogFooter>
            <Button data-testid="save-visit-button" onClick={saveVisit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              {busy ? "Speichern…" : "Besuch speichern & Punkte gutschreiben"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editVisitOpen} onOpenChange={setEditVisitOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Besuch bearbeiten</DialogTitle>
            <DialogDescription>Dienstleistungen, Preise, Notiz und Punkte anpassen. Bei Punkteänderung wird nur die Differenz verrechnet.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-3" data-testid="edit-visit-service-picker">
              {activeCats.map((cat) => {
                const svcs = (catalog.services || []).filter((s) => s.active && s.category_id === cat.id).sort((a, b) => a.order - b.order);
                if (svcs.length === 0) return null;
                return (
                  <div key={cat.id}>
                    <p className="text-[11px] font-semibold uppercase tracking-widest text-amber-800/80 mb-1.5">{cat.name}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {svcs.map((s) => {
                        const on = editSelected.some((x) => x.service_id === s.id);
                        return (
                          <button key={s.id} type="button" data-testid={`edit-service-chip-${s.id}`} onClick={() => toggleEditService(s, cat.name)}
                            className={`text-xs px-2.5 py-1.5 rounded-full border transition-colors ${on ? "bg-[#4A1D24] text-white border-[#4A1D24]" : "bg-white text-stone-600 border-stone-200 hover:border-[#C59B6C]"}`}>
                            {s.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            {editSelected.length > 0 && (
              <div className="rounded-xl border border-stone-200 p-3 space-y-2" data-testid="edit-visit-price-fields">
                {editSelected.map((x) => (
                  <div key={x.service_id} className="flex items-center gap-2">
                    <button type="button" data-testid={`edit-remove-${x.service_id}`} onClick={() => removeEditService(x.service_id)} className="text-rose-500 hover:text-rose-700 shrink-0"><X className="h-4 w-4" /></button>
                    <span className="flex-1 text-sm text-stone-700 min-w-0 truncate">{x.name}</span>
                    <div className="relative w-28 shrink-0">
                      <Input data-testid={`edit-price-input-${x.service_id}`} type="number" min="0" step="0.01" value={x.price} onChange={(e) => setEditPrice(x.service_id, e.target.value)} className="pr-6 text-right" placeholder="0,00" />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-400 text-sm">€</span>
                    </div>
                  </div>
                ))}
                <div className="flex justify-between items-center border-t border-stone-200 pt-2 font-semibold text-stone-900">
                  <span>Gesamtpreis</span>
                  <span data-testid="edit-visit-total-price" className="font-mono">{eur(editVisitTotal)}</span>
                </div>
              </div>
            )}

            <div className="rounded-xl bg-amber-50 border border-amber-200 p-3">
              <Label className="text-xs flex items-center gap-1 text-amber-900"><Coins className="h-3.5 w-3.5" /> Gutgeschriebene Punkte</Label>
              <Input data-testid="edit-visit-points-input" type="number" min="0" value={editPoints} onChange={(e) => setEditPoints(e.target.value)} className="mt-1 bg-white" />
              <p className="text-[11px] text-amber-800/80 mt-1">Es wird nur die Differenz zum bisherigen Wert auf den Punktestand angewendet.</p>
            </div>

            <div><Label className="text-xs">Interne Notiz / Bemerkung</Label><Textarea data-testid="edit-visit-comment-input" value={editComment} onChange={(e) => setEditComment(e.target.value)} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditVisitOpen(false)}>Abbrechen</Button>
            <Button data-testid="save-edit-visit-button" onClick={saveEditVisit} className="bg-emerald-600 hover:bg-emerald-700 text-white">Änderungen speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Kundin bearbeiten</DialogTitle>
            <DialogDescription>Kontaktdaten und Marketing-Einwilligung aktualisieren.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Vorname</Label><Input data-testid="edit-firstname-input" value={editForm.first_name} onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })} /></div>
              <div><Label className="text-xs">Nachname</Label><Input data-testid="edit-lastname-input" value={editForm.last_name} onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })} /></div>
            </div>
            <div><Label className="text-xs">E-Mail</Label><Input data-testid="edit-email-input" type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} /></div>
            <div><Label className="text-xs">Telefon</Label><Input data-testid="edit-phone-input" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} /></div>
            <div className="flex items-center justify-between rounded-lg border border-stone-200 p-3">
              <span className="text-sm text-stone-600">Marketing-Einwilligung</span>
              <Switch data-testid="edit-marketing-switch" checked={editForm.marketing_consent} onCheckedChange={(v) => setEditForm({ ...editForm, marketing_consent: v })} />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="save-edit-button" onClick={saveEdit} className="bg-[#4A1D24] hover:bg-[#3a161c] text-white">Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-rose-700">Kundin löschen</DialogTitle>
            <DialogDescription>Möchten Sie diese Kundin wirklich dauerhaft löschen? Diese Aktion kann nicht rückgängig gemacht werden.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-stone-600">Geben Sie zur Bestätigung <span className="font-mono font-semibold">LÖSCHEN</span> ein:</p>
            <Input data-testid="delete-confirm-input" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="LÖSCHEN" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>Abbrechen</Button>
            <Button data-testid="confirm-delete-customer-button" onClick={deleteCustomer} disabled={confirmText !== "LÖSCHEN"} className="bg-rose-600 hover:bg-rose-700 text-white">Endgültig löschen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-stone-500">{label}</span>
      <span className="text-stone-800 font-medium truncate max-w-[60%] text-right">{value}</span>
    </div>
  );
}
